import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Upload, X, Image, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImageUpload({ value, onChange, multiple = false, label = 'Bild' }) {
  const [mode, setMode] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const fileRef = useRef(null);
  const token = localStorage.getItem('itcs-admin-token');

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      if (multiple) {
        const formData = new FormData();
        files.forEach(f => formData.append('files', f));
        const res = await axios.post(`${API}/upload/multiple`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        const currentImages = Array.isArray(value) ? value : [];
        onChange([...currentImages, ...res.data.urls]);
      } else {
        const formData = new FormData();
        formData.append('file', files[0]);
        const res = await axios.post(`${API}/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        onChange(res.data.url);
      }
      toast.success('Bild hochgeladen');
    } catch {
      toast.error('Fehler beim Hochladen');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const addUrl = () => {
    if (!urlInput.trim()) return;
    if (multiple) {
      const currentImages = Array.isArray(value) ? value : [];
      onChange([...currentImages, urlInput.trim()]);
    } else {
      onChange(urlInput.trim());
    }
    setUrlInput('');
  };

  const removeImage = (index) => {
    if (multiple && Array.isArray(value)) {
      onChange(value.filter((_, i) => i !== index));
    } else {
      onChange('');
    }
  };

  const images = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  return (
    <div data-testid="image-upload">
      <label className="text-xs font-semibold text-slate-700 block mb-2">{label}</label>

      {/* Mode Toggle */}
      <div className="flex gap-1 mb-3">
        <button
          type="button"
          onClick={() => setMode('upload')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${mode === 'upload' ? 'bg-[#066ce7] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="upload-mode-btn"
        >
          <Upload className="w-3 h-3" /> Upload
        </button>
        <button
          type="button"
          onClick={() => setMode('url')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${mode === 'url' ? 'bg-[#066ce7] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="url-mode-btn"
        >
          <Link2 className="w-3 h-3" /> URL
        </button>
      </div>

      {/* Upload */}
      {mode === 'upload' && (
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-slate-300 rounded-lg p-5 text-center cursor-pointer hover:border-[#066ce7] hover:bg-blue-50/30 transition-all"
          data-testid="upload-dropzone"
        >
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif" multiple={multiple} onChange={handleFileUpload} className="hidden" />
          {uploading ? (
            <p className="text-sm text-slate-500">Wird hochgeladen...</p>
          ) : (
            <>
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Klicken zum Hochladen</p>
              <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP, GIF (max. 15 MB)</p>
            </>
          )}
        </div>
      )}

      {/* URL Input */}
      {mode === 'url' && (
        <div className="flex gap-2">
          <Input value={urlInput} onChange={e => setUrlInput(e.target.value)} placeholder="https://..." className="text-sm" data-testid="url-input" />
          <Button type="button" variant="outline" onClick={addUrl} data-testid="add-url-btn">+</Button>
        </div>
      )}

      {/* Preview */}
      {images.length > 0 && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {images.map((img, i) => (
            <div key={i} className="relative w-24 h-20 rounded-lg overflow-hidden group border border-slate-200" data-testid={`image-preview-${i}`}>
              <img src={resolveUrl(img)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%2394a3b8" font-size="12">Bild</text></svg>'; }} />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeImage(i); }}
                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-image-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
